import { InjectionToken } from '@angular/core';
import { DeviceType } from '@ktypes/enums';
import { Deserializable, JsonObject, Serializable } from '@ktypes/models';
import { DateTimeUtil } from '@kutil';
import { AnalyticEvent } from './enums/analytic-event.enum';
import { AnalyticsCategory } from './enums/analytics-category.enum';
import { AnalyticsPageName } from './enums/analytics-page-name.enum';
import { PageMappingData } from './page-mapping-info.model';

export const PAGE_MAPPING = new InjectionToken<PageMappingData>('PageMappingData');

export interface AnalyticsData {
  page?: AnalyticsPageName;
  event?: AnalyticEvent;
  meta?: JsonObject;
}

export class AnalyticsEvent implements Deserializable<any, AnalyticsEvent>, Serializable<JsonObject> {
  // generated
  public appVersion: string;
  public deviceTimestamp: string;
  public platform = DeviceType.WEB;
  public platformVersion: string;
  public timezone: string;

  constructor(
    public page?: AnalyticsPageName,
    public url?: string,
    public category?: AnalyticsCategory,
    public section?: string,
    public event?: AnalyticEvent,
    public label?: string,
    public value?: number,
    public meta: JsonObject = {}
  ) {
    this.deviceTimestamp = DateTimeUtil.formatInLocal(new Date());
    this.timezone = DateTimeUtil.getTimeZone();
    this.platform = DeviceType.WEB;

    // these values come from the constructor
    this.page = page;
    this.url = url;
    this.category = category;
    this.section = section;
    this.event = event;
    this.label = label;
    this.meta = meta;
  }

  deserialize(input: JsonObject, existingData?: AnalyticsEvent): AnalyticsEvent {
    if (input == null) {
      return null;
    }

    this.page = this._getValueOrExisting('page', input, existingData);
    this.url = this._getValueOrExisting('url', input, existingData);
    this.category = this._getValueFromEnum(AnalyticsCategory, 'category', input, existingData);
    this.section = this._getValueOrExisting('section', input, existingData);
    this.event = this._getValueFromEnum(AnalyticEvent, 'event', input, existingData);
    this.label = this._getValueOrExisting('label', input, existingData);
    this.value = this._getValueOrExisting('value', input, existingData);
    this.meta = this._getValueOrExisting('meta', input, existingData);
    this.deviceTimestamp = this._getValueOrExisting('deviceTimestamp', input, existingData);
    this.timezone = this._getValueOrExisting('timezone', input, existingData);
    this.platform = this._getValueFromEnum(DeviceType, 'platform', input, existingData) || DeviceType.WEB;
    this.platformVersion = this._getValueOrExisting('platformVersion', input, existingData);
    this.appVersion = this._getValueOrExisting('appVersion', input, existingData);
    this.platformVersion = this._getValueOrExisting('platformVersion', input, existingData);

    return this;
  }

  serialize(): JsonObject {
    const json: JsonObject = {};
    json['deviceTimestamp'] = this.deviceTimestamp;
    json['timezone'] = this.timezone;
    json['platform'] = this.platform;
    json['platformVersion'] = this.platformVersion;
    json['appVersion'] = this.appVersion;
    json['page'] = this.page;
    json['category'] = this.category;
    json['section'] = this.section;
    json['event'] = this.event;
    switch (this.event) {
      case AnalyticEvent.app_open:
        json['event'] = 'App Open';
        break;
      case AnalyticEvent.app_exit:
        json['event'] = 'App Exit';
        break;
    }
    json['label'] = this.label;
    json['value'] = this.value;
    json['meta'] = this.meta;
    return json;
  }

  private _getValueOrExisting(key: string, input: JsonObject, existing: AnalyticsEvent) {
    const existingData = existing == null ? this : existing;
    return input[key] || existingData?.[key];
  }

  private _getValueFromEnum(values: any, key: string, input: JsonObject, existing: AnalyticsEvent) {
    const existingData = existing == null ? this : existing;
    return values[input[key]] || (existingData && values[existingData[key]]);
  }
}
